import React, { useState, useEffect } from "react";
import { Button, Dropdown, Message } from "semantic-ui-react";
import { useDispatch, useSelector } from "react-redux";
import {
  addDataSet,
  editDataset,
  fetchExternalDataset,
} from "../../actions/dataConsoleAction";
import { getDataSet } from "../../apis"; // ✅ fallback generic API

const ExternalDatasetSettings = ({ showToaster, setShowLoader }) => {
  const [datasets, setDatasets] = useState([]);
  const [selectedDatasetId, setSelectedDatasetId] = useState("");
  const [currentExternalDataset, setCurrentExternalDataset] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isSaveDisabled, setIsSaveDisabled] = useState(false);

  const dispatch = useDispatch();
  const token = useSelector((state) => state.auth.token);

  useEffect(() => {
    console.log("[DEBUG] ExternalDatasetSettings mounted, calling loadDatasets()");
    loadDatasets();
  }, []);

  const loadDatasets = async () => {
    console.log("[DEBUG] loadDatasets() triggered");
    try {
      // ✅ Start loader ONCE at the beginning
      if (setShowLoader) setShowLoader(true);

      console.log("[DEBUG] Redux token value:", token);

      // 🔑 Normalize token
      let tokenString = "";
      if (typeof token === "string") {
        tokenString = token;
      } else if (token?.accessToken) {
        tokenString = token.accessToken;
      } else if (token?.idToken) {
        tokenString = token.idToken;
      } else {
        tokenString = localStorage.getItem("token") || "";
      }

      if (!tokenString) {
        console.warn("[WARN] No valid token string found, skipping external dataset fetch");
        return;
      }

      // ✅ Always prepend Bearer
      const bearerToken = `Bearer ${tokenString}`;
      console.log("[DEBUG] Dispatching fetchExternalDataset with token:", bearerToken.substring(0, 30) + "...");

      let datasets = await dispatch(fetchExternalDataset(bearerToken));

      // ✅ If no external datasets, call fallback once (without restarting loader)
      if (!datasets || datasets.length === 0) {
        console.warn("[WARN] External dataset API returned empty, falling back to generic dataset API");

        const genericResponse = await getDataSet();
        datasets = genericResponse?.data || [];

        if (datasets.length > 0) {
          console.log("[DEBUG] Triggering toaster for no external datasets found");
          // showToaster("No external datasets found, showing all datasets instead", "warning");
        }
      }

      console.log("[DEBUG] Final dataset list after fallback:", datasets);

      // ✅ Handle datasets normally
      if (datasets && datasets.length > 0) {
        setDatasets(datasets);
        const external = datasets.find((d) => d.isExternalDataset === true);
        if (external) {
          setCurrentExternalDataset(external);
          setSelectedDatasetId(external.id);
          setIsSaveDisabled(true);
        } else {
          setCurrentExternalDataset(null);
          setSelectedDatasetId("");
          setIsSaveDisabled(false);
        }
      } else {
        console.warn("[WARN] No datasets found in either external or generic API");
        setDatasets([]);
        setCurrentExternalDataset(null);
        setSelectedDatasetId("");
        setIsSaveDisabled(false);
      }
    } catch (error) {
      console.error("[ERROR] loadDatasets failed:", error);
      showToaster("Failed to load datasets", "error");
    } finally {
      // ✅ Stop loader only once, after everything is done (external + fallback)
      if (setShowLoader) setShowLoader(false);
    }
  };

  const handleSave = async () => {
    if (!selectedDatasetId) {
      showToaster("Please select a dataset", "error");
      return;
    }

    setLoading(true);
    try {
      const selectedDataset = datasets.find((d) => d.id === selectedDatasetId);

      const payload = {
        datasetName: selectedDataset.datasetName,
        description: selectedDataset.description || "",
        isExternalDataset: true,
        checkdatasetName: false,
      };

      await dispatch(editDataset(payload, selectedDataset.id, true));

      setCurrentExternalDataset(selectedDataset);
      setIsSaveDisabled(true);
      showToaster(
        `External dataset set to "${selectedDataset.datasetName}" successfully`,
        "success"
      );
    } catch (error) {
      console.error("Error setting external dataset:", error);
      showToaster("Failed to set external dataset", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleClear = async () => {
  if (!currentExternalDataset) return;

  setLoading(true);
  try {
    const updatePayload = {
      datasetName: currentExternalDataset.datasetName,
      description: currentExternalDataset.description || "",
      isExternalDataset: false,
      checkdatasetName: false,
    };

    await dispatch(editDataset(updatePayload, currentExternalDataset.id));

    // ✅ Clear UI states
    setCurrentExternalDataset(null);
    setSelectedDatasetId("");
    setIsSaveDisabled(false);

    // ✅ Immediately refresh datasets from backend
    await loadDatasets();

    showToaster("External dataset cleared successfully", "success");
  } catch (error) {
    console.error("Error clearing external dataset:", error);
    showToaster("Failed to clear external dataset", "error");
  } finally {
    setLoading(false);
  }
};


  const datasetOptions = datasets.map((dataset) => ({
    key: dataset.id,
    value: dataset.id,
    text: dataset.datasetName,
    description: dataset.isExternalDataset
      ? "External Dataset ✓"
      : `Created: ${new Date(
          dataset.createdUTC || dataset.createdAt
        ).toLocaleDateString()}`,
  }));

  return (
    <div className="configuration-segment" style={{ marginTop: "30px" }}>
      <h2 style={{ marginBottom: "20px" }}>External Dataset</h2>

      {currentExternalDataset && (
        <div
          style={{
            backgroundColor: "#f8f9fa",
            border: "1px solid #28a745",
            borderRadius: "16px",
            padding: "16px 20px",
            marginBottom: "20px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: "12px",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <div
              style={{
                backgroundColor: "#28a745",
                color: "white",
                borderRadius: "50%",
                width: "24px",
                height: "24px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "14px",
                fontWeight: "bold",
              }}
            >
              ✓
            </div>
            <div>
              <div
                style={{
                  fontWeight: "600",
                  color: "#2c3e50",
                  fontSize: "14px",
                  marginBottom: "2px",
                }}
              >
                External Dataset Active
              </div>
              <div
                style={{
                  color: "#28a745",
                  fontSize: "16px",
                  fontWeight: "500",
                }}
              >
                {currentExternalDataset.datasetName}
              </div>
            </div>
          </div>
          <div
            style={{
              color: "#6c757d",
              fontSize: "13px",
              fontStyle: "italic",
            }}
          >
            Set on{" "}
            {new Date(
              currentExternalDataset.createdUTC ||
                currentExternalDataset.createdAt
            ).toLocaleDateString("en-US", {
              month: "short",
              day: "numeric",
              year: "numeric",
            })}
          </div>
        </div>
      )}

      <div className="config-item">
        <label style={{ display: "block", marginBottom: "8px" }}>
          Select Dataset
        </label>
        <Dropdown
          placeholder="Choose a dataset for external users"
          fluid
          search
          selection
          clearable
          options={datasetOptions}
          value={selectedDatasetId}
          onChange={(e, { value }) => {
            setSelectedDatasetId(value);
            setIsSaveDisabled(false);
          }}
          disabled={loading}
        />
        {datasets.length === 0 && (
          <Message warning style={{ marginTop: "10px" }}>
            <Message.Header>No Datasets Available</Message.Header>
            <p>Please create a dataset first before configuring external.</p>
          </Message>
        )}
      </div>

      <div className="button-group" style={{ marginTop: "15px" }}>
        <Button
          className={`save-button ${isSaveDisabled ? "disabled-button" : ""}`}
          onClick={handleSave}
          disabled={!selectedDatasetId || loading || isSaveDisabled}
          loading={loading}
          style={{ minWidth: "80px" }}
        >
          Save
        </Button>

        {currentExternalDataset && (
          <Button
            className="delete-button"
            onClick={handleClear}
            disabled={loading}
            loading={loading}
            style={{ minWidth: "80px" }}
          >
            Clear
          </Button>
        )}
      </div>
    </div>
  );
};

export default ExternalDatasetSettings;
